import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import Contact from '../../models/Contact';
import { EntityType } from '../../models/InvestmentEntity';
import InvestmentEntityContact, {
  ContactRole,
  RepresentativeType,
} from '../../models/InvestmentEntityContact';
import CompactCardSelector from '../common/CompactCardSelector';
import { StyledButton, StyledButtonGroup } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import {
  StyledCard,
  StyledCardContent,
  StyledFieldRow,
  StyledHeading,
  StyledLabel,
  StyledTypography,
} from '../styles/styles';
import { AddContactModalState } from './contactAssociationTypes';

const REPRESENTATIVE_TYPE_OPTIONS = [
  { value: 'FAMILY', label: 'Family' },
  { value: 'WEALTH_MANAGER', label: 'Wealth Manager' },
  { value: 'ADVISOR', label: 'Advisor' },
  { value: 'ACCOUNTANT', label: 'Accountant' },
  { value: 'OTHER', label: 'Other' },
] as const;

export function ContactAssociationLoadingCard() {
  return (
    <StyledCard tone="tone1">
      <StyledCardContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </StyledCardContent>
    </StyledCard>
  );
}

export interface ContactAssociationSharedHeaderProps {
  entityName: string;
  entityType: EntityType;
  error: string | null;
  validationErrors: string[];
}

export function ContactAssociationSharedHeader({
  entityName,
  entityType,
  error,
  validationErrors,
}: ContactAssociationSharedHeaderProps) {
  return (
    <>
      <StyledHeading headerVariant="heading2" sx={{ mb: 2 }}>
        Associate Contacts
      </StyledHeading>

      <StyledTypography sx={{ mb: 4 }}>
        Associate contacts with the investment entity "{entityName}". Different contact roles are
        available for {entityType.toLowerCase()} entities.
      </StyledTypography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Contact Requirements:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {validationErrors.map((validationError, index) => (
              <li key={index}>{validationError}</li>
            ))}
          </ul>
        </Alert>
      )}
    </>
  );
}

export interface ContactAssociationAssociatedContactsProps {
  associatedContacts: InvestmentEntityContact[];
  renderAssociatedContactCard: (contact: InvestmentEntityContact) => React.ReactNode;
}

export function ContactAssociationAssociatedContacts({
  associatedContacts,
  renderAssociatedContactCard,
}: ContactAssociationAssociatedContactsProps) {
  if (associatedContacts.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Associated Contacts ({associatedContacts.length})
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {associatedContacts
          .filter((contact) => contact.contact)
          .map((contact) => renderAssociatedContactCard(contact))}
      </Box>
    </Box>
  );
}

export interface ContactAssociationSearchSectionProps {
  searchInputRef: React.RefObject<HTMLInputElement>;
  searchQuery: string;
  searchLoading: boolean;
  searchResults: Contact[];
  addContactSelected: boolean;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  renderContactCard: (contact: Contact, isSearchResult: boolean) => React.ReactNode;
}

export function ContactAssociationSearchSection({
  searchInputRef,
  searchQuery,
  searchLoading,
  searchResults,
  addContactSelected,
  onSearchChange,
  renderContactCard,
}: ContactAssociationSearchSectionProps) {
  if (addContactSelected) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Search and Add Contacts
      </Typography>

      <StyledInput
        ref={searchInputRef}
        fullWidth
        variant="outlined"
        placeholder="Search contacts by name, email, or mobile..."
        value={searchQuery}
        onChange={onSearchChange}
        sx={{ mb: 2 }}
      />

      {searchLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Searching...</Typography>
        </Box>
      )}

      {!searchLoading && searchResults.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {searchResults.map((contact) => renderContactCard(contact, true))}
        </Box>
      )}

      {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No available contacts found. All matching contacts may already be associated with this
          entity.
        </Typography>
      )}
    </Box>
  );
}

export interface ContactAssociationRoleSelectionSectionProps {
  entityName: string;
  addContactModal: AddContactModalState;
  associating: boolean;
  availableRoles: ContactRole[];
  otherRepresentativeTypeRef: React.RefObject<HTMLInputElement>;
  onRoleChange: (role: ContactRole) => void;
  onRepresentativeTypeChange: (representativeType: RepresentativeType) => void;
  onOtherRepresentativeTypeChange: (value: string) => void;
  onConfirmAddContact: () => void;
  onCancelAddContact: () => void;
}

export function ContactAssociationRoleSelectionSection({
  entityName,
  addContactModal,
  associating,
  availableRoles,
  otherRepresentativeTypeRef,
  onRoleChange,
  onRepresentativeTypeChange,
  onOtherRepresentativeTypeChange,
  onConfirmAddContact,
  onCancelAddContact,
}: ContactAssociationRoleSelectionSectionProps) {
  if (!addContactModal.contact) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Add {addContactModal.contact.name} to {entityName}
      </Typography>

      <CompactCardSelector
        label="Contact Role"
        required
        options={availableRoles.map((role) => ({
          value: role,
          label: role.replace('_', ' '),
        }))}
        value={addContactModal.role}
        onChange={(value) => onRoleChange(value as ContactRole)}
      />

      {addContactModal.role === 'REPRESENTATIVE' && (
        <>
          <CompactCardSelector
            label="Representative Type"
            required
            options={[...REPRESENTATIVE_TYPE_OPTIONS]}
            value={addContactModal.representativeType || ''}
            onChange={(value) => onRepresentativeTypeChange(value as RepresentativeType)}
          />
          {addContactModal.representativeType === 'OTHER' && (
            <StyledFieldRow stacked>
              <StyledLabel stacked>Other Representative Type</StyledLabel>
              <StyledInput
                ref={otherRepresentativeTypeRef}
                value={addContactModal.otherRepresentativeType || ''}
                onChange={(event) => onOtherRepresentativeTypeChange(event.target.value)}
                fullWidth
              />
            </StyledFieldRow>
          )}
        </>
      )}

      <StyledButtonGroup justifyContent="center" sx={{ mt: 3 }}>
        <StyledButton variant="contained" onClick={onConfirmAddContact} disabled={associating}>
          {associating ? 'Adding Contact...' : 'Add Contact to Entity'}
        </StyledButton>
        <StyledButton variant="outlined" onClick={onCancelAddContact} disabled={associating}>
          Cancel Add Contact
        </StyledButton>
      </StyledButtonGroup>
    </Box>
  );
}

export interface ContactAssociationActionButtonsProps {
  addContactSelected: boolean;
  associatedContactCount: number;
  associating: boolean;
  validationErrors: string[];
  canCompleteWithoutContacts: boolean;
  onComplete: () => void;
  onSkip?: () => void;
}

export function ContactAssociationActionButtons({
  addContactSelected,
  associatedContactCount,
  associating,
  validationErrors,
  canCompleteWithoutContacts,
  onComplete,
  onSkip,
}: ContactAssociationActionButtonsProps) {
  if (addContactSelected) return null;

  const skipButtonLabel =
    associatedContactCount > 0 ? 'Skip Additional Contacts' : 'Skip Contact Association';

  return (
    <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
      <StyledButton
        variant="contained"
        onClick={onComplete}
        disabled={associating || (validationErrors.length > 0 && !canCompleteWithoutContacts)}
      >
        Complete Entity Setup
      </StyledButton>
      {onSkip && canCompleteWithoutContacts && (
        <StyledButton variant="outlined" onClick={onSkip} disabled={associating}>
          {skipButtonLabel}
        </StyledButton>
      )}
    </Box>
  );
}
